import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Coins, Eye, HandCoins, Landmark, Loader2, Search, X } from "lucide-react";
import { useCommissions } from "../hooks/useCommissions";
import {
  EmptyState,
  ErrorState,
  FilterTabs,
  LoadingState,
  MetricCard,
  PageHeader,
  StatusPill,
  formatDate,
  formatMoney,
} from "../components/PageKit";

const STATUS = {
  pending_payment: { label: "En attente de paiement", tone: "warning" },
  processing_payment: { label: "Paiement en cours", tone: "info" },
  paid: { label: "Payée", tone: "success" },
  payment_failed: { label: "Échec de paiement", tone: "danger" },
  cancelled: { label: "Annulée", tone: "neutral" },
};
const ATTEMPT = {
  success: { label: "Réussi", tone: "success" },
  failed: { label: "Échoué", tone: "danger" },
  pending: { label: "En cours", tone: "warning" },
};

const isBalanceIssue = (attempt) =>
  /insufficient|solde|balance/i.test(String(attempt?.error_message || "")) ||
  Number(attempt?.available_balance ?? Infinity) < Number(attempt?.required_amount ?? 0);

function PaymentAttemptsModal({ state, onClose }) {
  if (!state.open) return null;
  const attempts = state.attempts || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="modal-surface max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Tentatives de paiement</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Fermer">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        <div className="space-y-3 p-5">
          {state.loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
          ) : !attempts.length ? (
            <EmptyState icon={Coins} title="Aucune tentative" message="Le prélèvement n'a pas encore été tenté." />
          ) : (
            attempts.map((a) => {
              const s = ATTEMPT[a.status] || { label: a.status, tone: "neutral" };
              return (
                <div key={a.id} className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
                  <div className="mb-2 flex items-center justify-between">
                    <StatusPill tone={s.tone}>{s.label}</StatusPill>
                    <span className="text-xs text-slate-400">{new Date(a.attempted_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-200">Montant requis : <strong>{formatMoney(a.required_amount)}</strong></p>
                  <p className="text-sm text-slate-500">Solde disponible : {formatMoney(a.available_balance)}</p>
                  {a.status === "failed" && isBalanceIssue(a) ? (
                    <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        Solde insuffisant : rechargez votre portefeuille, le prélèvement sera retenté.{" "}
                        <Link to="/admin-marketplace/wallet" className="font-semibold underline">Recharger</Link>
                      </span>
                    </div>
                  ) : a.error_message ? (
                    <p className="mt-1 text-sm text-rose-600">{a.error_message}</p>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminMarketplaceCommissions() {
  const { commissions, stats, loading, error, fetchCommissions, fetchStats, fetchPaymentAttempts } = useCommissions();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [attempts, setAttempts] = useState({ open: false, loading: false, attempts: null });

  useEffect(() => {
    fetchCommissions();
    fetchStats();
  }, [fetchCommissions, fetchStats]);

  const counts = useMemo(() => {
    const out = { all: commissions.length };
    commissions.forEach((c) => {
      out[c.status] = (out[c.status] || 0) + 1;
    });
    return out;
  }, [commissions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commissions.filter(
      (c) => (status === "all" || c.status === status) && (!q || String(c.order_id || "").toLowerCase().includes(q))
    );
  }, [commissions, status, search]);

  const openAttempts = async (id) => {
    setAttempts({ open: true, loading: true, attempts: null });
    try {
      const data = await fetchPaymentAttempts(id);
      const list = Array.isArray(data) ? data : data?.results || data?.attempts || [];
      setAttempts({ open: true, loading: false, attempts: list });
    } catch {
      setAttempts({ open: true, loading: false, attempts: [] });
    }
  };

  if (loading && !commissions.length) return <LoadingState label="Chargement des commissions…" />;
  if (error && !commissions.length) return <ErrorState error={error} title="Commissions indisponibles" onRetry={() => fetchCommissions()} />;

  const failedCount = counts.payment_failed || 0;

  return (
    <div className="page-shell space-y-5">
      <PageHeader
        eyebrow="Marketplace"
        title="Commissions"
        subtitle="Une commission est due pour chaque commande confirmée par un prestataire puis livrée."
      />

      {failedCount > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <span className="flex-1">
            {failedCount} commission(s) n'ont pas pu être prélevées. Vérifiez le solde de votre portefeuille.
          </span>
          <Link to="/admin-marketplace/wallet" className="btn-outline">Portefeuille</Link>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <MetricCard icon={Coins} label="Total" value={formatMoney(stats.total_amount)} />
          <MetricCard icon={HandCoins} label="Part prestataires" value={formatMoney(stats.provider_amount)} tone="success" />
          <MetricCard icon={Landmark} label="Frais plateforme" value={formatMoney(stats.saas_amount)} tone="info" />
          <MetricCard icon={Coins} label="Commissions" value={stats.count ?? 0} tone="neutral" />
        </div>
      )}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <FilterTabs
          value={status}
          onChange={setStatus}
          tabs={[
            { key: "all", label: "Toutes", count: counts.all },
            ...Object.entries(STATUS).map(([key, s]) => ({ key, label: s.label, count: counts[key] || 0 })),
          ]}
        />
        <label className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="N° de commande…"
            className="w-56 rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm dark:border-slate-700" />
        </label>
      </div>

      <div className="surface-card overflow-hidden rounded-xl border border-slate-200 bg-white p-0 dark:border-slate-800 dark:bg-slate-900">
        {!filtered.length ? (
          <div className="p-4">
            <EmptyState icon={Coins} title="Aucune commission"
              message={commissions.length ? "Aucune commission ne correspond à ces filtres." : "Les commissions apparaissent quand une commande confiée à un prestataire est livrée."} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                  <th className="px-4 py-3">Commande</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Prestataire</th>
                  <th className="px-4 py-3 text-right">Plateforme</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Payée le</th>
                  <th className="px-4 py-3 text-right">Paiement</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const s = STATUS[c.status] || { label: c.status, tone: "neutral" };
                  return (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800">
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">#{String(c.order_id || "").slice(0, 8)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">{formatMoney(c.commission_total)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{formatMoney(c.commission_provider)}</td>
                      <td className="px-4 py-3 text-right text-indigo-600">{formatMoney(c.commission_saas)}</td>
                      <td className="px-4 py-3"><StatusPill tone={s.tone}>{s.label}</StatusPill></td>
                      <td className="px-4 py-3 text-xs text-slate-500">{formatDate(c.paid_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button type="button" onClick={() => openAttempts(c.id)} className="icon-button !h-8 !w-8 rounded-lg" title="Tentatives de paiement">
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PaymentAttemptsModal state={attempts} onClose={() => setAttempts({ open: false, loading: false, attempts: null })} />
    </div>
  );
}
